import {
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Input, Textarea } from '@heroui/input';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/modal';
import { Pagination } from '@heroui/pagination';
import { Select, SelectItem } from '@heroui/select';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { useLocale } from '../../contexts/LocaleContext';
import { adminService } from '../../services/adminService';
import { orderService } from '../../services/orderService';
import { AdminOrder, OrderFilters } from '../../types/admin';
import { OrderStatusObject } from '../../types/orders';
import { generateCanonicalUrl } from '../../utils/seoUtils';

const AdminOrders: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<AdminOrder | null>(null);
  const [orderStatuses, setOrderStatuses] = useState<OrderStatusObject[]>([]);
  const { formatCurrency, convertPrice } = useLocale();

  const {
    isOpen: isUpdateOpen,
    onOpen: onUpdateOpen,
    onClose: onUpdateClose,
  } = useDisclosure();

  const [filters, setFilters] = useState<OrderFilters>({
    page: parseInt(searchParams.get('page') || '1'),
    pageSize: 20,
    orderNumber: searchParams.get('orderNumber') || '',
    customerEmail: searchParams.get('customerEmail') || '',
    customerName: searchParams.get('customerName') || '',
    status: searchParams.get('status') || '',
    paymentStatus: searchParams.get('paymentStatus') || '',
    fromDate: searchParams.get('fromDate') || '',
    toDate: searchParams.get('toDate') || '',
  });

  const [updateForm, setUpdateForm] = useState({
    status: '',
    trackingNumber: '',
    notes: '',
  });

  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));

  const updateSearchParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.orderNumber) params.set('orderNumber', filters.orderNumber);
    if (filters.customerEmail)
      params.set('customerEmail', filters.customerEmail);
    if (filters.customerName) params.set('customerName', filters.customerName);
    if (filters.status) params.set('status', filters.status);
    if (filters.paymentStatus)
      params.set('paymentStatus', filters.paymentStatus);
    if (filters.fromDate) params.set('fromDate', filters.fromDate);
    if (filters.toDate) params.set('toDate', filters.toDate);
    if (filters.page > 1) params.set('page', filters.page.toString());
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const fetchOrderStatuses = useCallback(async () => {
    try {
      const statuses = await orderService.getOrderStatuses();
      setOrderStatuses(statuses);
    } catch (error) {
      toast.error('Failed to load order statuses');
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();

      if (filters.orderNumber)
        params.append('orderNumber', filters.orderNumber);
      if (filters.customerEmail)
        params.append('customerEmail', filters.customerEmail);
      if (filters.customerName)
        params.append('customerName', filters.customerName);
      if (filters.status) params.append('status', filters.status);
      if (filters.paymentStatus)
        params.append('paymentStatus', filters.paymentStatus);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);

      params.append('page', filters.page.toString());
      params.append('pageSize', filters.pageSize.toString());

      const response = await adminService.getAdminOrders(
        Object.fromEntries(params)
      );
      setOrders(response.orders);
      setTotalOrders(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Auto-apply filters: debounce loading when any filter changes
  useEffect(() => {
    const debounce = setTimeout(() => {
      updateSearchParams();
      loadOrders();
    }, 400);
    return () => clearTimeout(debounce);
  }, [filters, loadOrders, updateSearchParams]);

  useEffect(() => {
    fetchOrderStatuses();
  }, [fetchOrderStatuses]);

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      pageSize: 20,
      orderNumber: '',
      customerEmail: '',
      customerName: '',
      status: '',
      fromDate: '',
      toDate: '',
    });
    setSearchParams({});
    loadOrders();
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleUpdateOrder = (order: AdminOrder) => {
    setSelectedOrder(order);
    setUpdateForm({
      status: order.status,
      trackingNumber: order.trackingNumber || '',
      notes: order.notes || '',
    });
    onUpdateOpen();
  };

  const submitOrderUpdate = async () => {
    if (!selectedOrder) return;

    try {
      await adminService.updateOrderStatus(selectedOrder.id, updateForm);
      toast.success('Order updated successfully');
      onUpdateClose();
      loadOrders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update order');
    }
  };

  const sendStatusEmail = async (orderId: number) => {
    try {
      await adminService.sendOrderStatusEmail(orderId);
      toast.success('Status email sent successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send email');
    }
  };

  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'primary';
      case 'printing':
        return 'secondary';
      case 'printed':
        return 'secondary';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'refunded':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'paid':
        return 'success';
      case 'failed':
        return 'danger';
      case 'refunded':
        return 'danger';
      default:
        return 'default';
    }
  };

  return (
    <div className='space-y-6'>
      <SEOHead
        title='Admin Orders'
        description='Manage and track customer orders in the admin panel.'
        url={generateCanonicalUrl('/admin/orders')}
        type='website'
      />
      {/* Header */}
      <div className='md:flex md:items-center md:justify-between'>
        <div className='flex-1 min-w-0'>
          <h2 className='text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate'>
            Orders Management
          </h2>
          <p className='mt-1 text-sm text-gray-500'>
            Manage and track all customer orders
          </p>
        </div>
        <div className='mt-4 flex md:mt-0 md:ml-4'>
          <Button
            color='primary'
            startContent={<PlusIcon className='h-4 w-4' />}
          >
            Export Orders
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <FunnelIcon className='h-5 w-5 text-gray-500' />
            <span className='font-medium'>Filters</span>
          </div>
        </CardHeader>
        <CardBody>
          <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4'>
            <Input
              placeholder='Order Number'
              value={filters.orderNumber}
              onChange={e =>
                setFilters(prev => ({
                  ...prev,
                  orderNumber: e.target.value,
                  page: 1,
                }))
              }
              startContent={
                <MagnifyingGlassIcon className='h-4 w-4 text-gray-400' />
              }
            />
            <Input
              placeholder='Customer Email'
              value={filters.customerEmail}
              onChange={e =>
                setFilters(prev => ({
                  ...prev,
                  customerEmail: e.target.value,
                  page: 1,
                }))
              }
            />
            <Input
              placeholder='Customer Name'
              value={filters.customerName}
              onChange={e =>
                setFilters(prev => ({
                  ...prev,
                  customerName: e.target.value,
                  page: 1,
                }))
              }
            />
            <Select
              placeholder='Status'
              selectedKeys={filters.status ? [filters.status] : []}
              items={orderStatuses}
              onSelectionChange={keys => {
                const selectedKey = Array.from(keys)[0] as string;
                setFilters(prev => ({
                  ...prev,
                  status: selectedKey || '',
                  page: 1,
                }));
              }}
            >
              {status => <SelectItem key={status.id}>{status.name}</SelectItem>}
            </Select>
            <Select
              placeholder='Payment Status'
              selectedKeys={
                filters.paymentStatus ? [filters.paymentStatus] : []
              }
              onSelectionChange={keys => {
                const selectedKey = Array.from(keys)[0] as string;
                setFilters(prev => ({
                  ...prev,
                  paymentStatus: selectedKey || '',
                  page: 1,
                }));
              }}
            >
              <SelectItem key='Pending'>Pending</SelectItem>
              <SelectItem key='Paid'>Paid</SelectItem>
              <SelectItem key='Failed'>Failed</SelectItem>
              <SelectItem key='Refunded'>Refunded</SelectItem>
            </Select>
            <Input
              type='date'
              placeholder='From Date'
              value={filters.fromDate}
              onChange={e =>
                setFilters(prev => ({
                  ...prev,
                  fromDate: e.target.value,
                  page: 1,
                }))
              }
            />
            <Input
              type='date'
              placeholder='To Date'
              value={filters.toDate}
              onChange={e =>
                setFilters(prev => ({
                  ...prev,
                  toDate: e.target.value,
                  page: 1,
                }))
              }
            />
          </div>
          <div className='flex gap-2 mt-4'>
            <Button variant='ghost' onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <span className='font-medium'>
              {totalOrders} Orders {filters.status && `(${filters.status})`}
            </span>
            <span className='text-sm text-gray-500'>
              Page {filters.page} of {totalPages}
            </span>
          </div>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className='text-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto' />
            </div>
          ) : orders.length === 0 ? (
            <div className='text-center py-8 text-gray-500'>
              No orders found matching your criteria
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='border-b border-gray-200'>
                  <tr>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      Order
                    </th>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      Customer
                    </th>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      Date
                    </th>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      SLA
                    </th>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      Amount
                    </th>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      Status
                    </th>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      Payment
                    </th>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      Items
                    </th>
                    <th className='text-left py-3 px-4 font-medium text-gray-900'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200'>
                  {orders.map(order => (
                    <tr key={order.id} className='hover:bg-gray-50'>
                      <td className='py-4 px-4'>
                        <div>
                          <Link
                            to={`/admin/orders/${order.id}`}
                            className='font-medium text-primary-600 hover:text-primary-700'
                          >
                            {order.orderNumber}
                          </Link>
                          {order.trackingNumber && (
                            <div className='text-xs text-gray-500'>
                              {order.trackingNumber}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className='py-4 px-4'>
                        <div>
                          <div className='font-medium text-gray-900'>
                            {order.customerName}
                          </div>
                          <div className='text-sm text-gray-500'>
                            {order.customerEmail}
                          </div>
                        </div>
                      </td>
                      <td className='py-4 px-4 text-sm text-gray-900'>
                        {new Date(order.orderDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                      <td className='py-4 px-4'>
                        {(() => {
                          const statusLower = order.status.toLowerCase();
                          const printEnd = order.estimatedPrintEndDate
                            ? new Date(order.estimatedPrintEndDate)
                            : undefined;
                          const ready = order.estimatedReadyDate
                            ? new Date(order.estimatedReadyDate)
                            : undefined;
                          const shipped = order.shippedDate
                            ? new Date(order.shippedDate)
                            : undefined;
                          const now = new Date();

                          let light: 'green' | 'orange' | 'red' = 'green';
                          let label = 'Within print window';

                          if (ready && !shipped && now > ready) {
                            light = 'red';
                            label = 'Past SLA';
                          } else if (
                            printEnd &&
                            now > printEnd &&
                            statusLower !== 'printed' &&
                            statusLower !== 'shipped' &&
                            statusLower !== 'delivered'
                          ) {
                            light = 'orange';
                            label = 'In preparation window';
                          }

                          return (
                            <div className='flex items-center gap-2'>
                              <span
                                aria-label={light}
                                className={
                                  light === 'green'
                                    ? 'inline-block h-3 w-3 rounded-full bg-green-500'
                                    : light === 'orange'
                                      ? 'inline-block h-3 w-3 rounded-full bg-amber-500'
                                      : 'inline-block h-3 w-3 rounded-full bg-red-600'
                                }
                              />
                              <div className='text-sm text-gray-700'>
                                {label}
                                {ready && (
                                  <span className='ml-2 text-gray-500'>
                                    ETA: {ready.toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className='py-4 px-4'>
                        <div className='font-medium text-gray-900'>
                          {formatAmount(order.totalAmount)}
                        </div>
                        <div className='text-xs text-gray-500'>
                          {order.totalQuantity} items
                        </div>
                        <div className='text-xs text-gray-500'>
                          ({order.itemCount} unique)
                        </div>
                      </td>
                      <td className='py-4 px-4'>
                        <Chip
                          size='sm'
                          color={getStatusColor(order.status)}
                          variant='flat'
                        >
                          {order.status}
                        </Chip>
                      </td>
                      <td className='py-4 px-4'>
                        <Chip
                          size='sm'
                          color={getPaymentStatusColor(order.paymentStatus)}
                          variant='flat'
                        >
                          {order.paymentStatus}
                        </Chip>
                      </td>
                      <td className='py-4 px-4 text-sm text-gray-900'>
                        {order.itemCount}
                      </td>
                      <td className='py-4 px-4'>
                        <div className='flex gap-2'>
                          <Button
                            as={Link}
                            to={`/admin/orders/${order.id}`}
                            size='sm'
                            variant='ghost'
                            isIconOnly
                          >
                            <EyeIcon className='h-4 w-4' />
                          </Button>
                          <Button
                            size='sm'
                            variant='ghost'
                            isIconOnly
                            onClick={() => handleUpdateOrder(order)}
                          >
                            <PencilIcon className='h-4 w-4' />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className='flex justify-center mt-6'>
              <Pagination
                total={totalPages}
                page={filters.page}
                onChange={handlePageChange}
              />
            </div>
          )}
        </CardBody>
      </Card>

      {/* Update Order Modal */}
      <Modal isOpen={isUpdateOpen} onClose={onUpdateClose} size='lg'>
        <ModalContent>
          <ModalHeader>Update Order {selectedOrder?.orderNumber}</ModalHeader>
          <ModalBody>
            <div className='space-y-4'>
              <Select
                label='Status'
                selectedKeys={[updateForm.status]}
                items={orderStatuses}
                onSelectionChange={keys => {
                  const selectedKey = Array.from(keys)[0] as string;
                  setUpdateForm(prev => ({ ...prev, status: selectedKey }));
                }}
              >
                {status => (
                  <SelectItem key={status.id}>{status.name}</SelectItem>
                )}
              </Select>

              <Input
                label='Tracking Number'
                placeholder='Enter tracking number'
                value={updateForm.trackingNumber}
                onChange={e =>
                  setUpdateForm(prev => ({
                    ...prev,
                    trackingNumber: e.target.value,
                  }))
                }
              />

              <Textarea
                label='Notes'
                placeholder='Add notes about this order'
                value={updateForm.notes}
                onChange={e =>
                  setUpdateForm(prev => ({ ...prev, notes: e.target.value }))
                }
                rows={3}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='ghost' onClick={onUpdateClose}>
              Cancel
            </Button>
            <Button color='primary' onClick={submitOrderUpdate}>
              Update Order
            </Button>
            {selectedOrder && (
              <Button
                color='secondary'
                onClick={() => sendStatusEmail(selectedOrder.id)}
              >
                Send Email
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AdminOrders;
